import "./config/agGridLicenseBootstrap.ts";
import "./config/muiLicenseBootstrap.ts";
import {
  Experimental_CssVarsProvider as CssVarsProvider,
  experimental_extendTheme as extendTheme,
} from "@mui/material";
import type { PaletteOptions } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { init, wrapCreateBrowserRouter } from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { routes } from "app/routes/routes.tsx";
import { store } from "app/store.ts";
import SessionTimeoutHandler from "components/organisms/SessionTimeoutHandler/SessionTimeoutHandler.tsx";
import VersionChangeHandler from "features/auth/components/VersionChangeHandler.tsx";
import {
  SESSION_TIMEOUT_MINUTES,
  SESSION_TIMEOUT_WARNING_SECONDS,
} from "./config/sessionTimeout.ts";
import "./index.css";
import EnvironmentDiagnosticsLogger from "features/environment/components/EnvironmentDiagnosticsLogger.tsx";
import PermissionImpersonationBanner from "features/permissions/components/PermissionImpersonationBanner.tsx";
import SysAdminPermissionInspector from "features/permissions/components/SysAdminPermissionInspector.tsx";

const shouldReloadForChunkFailure = (message?: string) =>
  Boolean(
    message &&
      /Failed to fetch dynamically imported module|Loading chunk|ChunkLoadError/i.test(
        message,
      ),
  );

const registerChunkLoadRecovery = () => {
  let hasReloaded = false;
  const reloadOnce = () => {
    if (hasReloaded) {
      return;
    }
    hasReloaded = true;
    window.location.reload();
  };

  window.addEventListener("vite:preloadError", reloadOnce);
  window.addEventListener("error", (event) => {
    if (event instanceof ErrorEvent) {
      const message = event.error?.message ?? event.message;
      if (shouldReloadForChunkFailure(message)) {
        reloadOnce();
      }
    }
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      typeof reason === "string" ? reason : reason?.message;
    if (shouldReloadForChunkFailure(message)) {
      reloadOnce();
    }
  });
};

registerChunkLoadRecovery();

// Routing (react-router wrapped in Sentry.io)
init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENVIRONMENT,
});
const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);
const router = sentryCreateBrowserRouter(routes, {
  future: { v7_startTransition: true },
});

// MUI
const darkPalette: PaletteOptions = {
  mode: "dark",
  primary: {
    main: "#5fa5f6",
  },
  secondary: {
    main: "#90caf9",
  },
};
const lightPalette: PaletteOptions = {
  mode: "light",
  primary: {
    main: "#1565c0",
  },
  secondary: {
    main: "#90caf9",
  },
};

const theme = extendTheme({
  colorSchemes: {
    dark: {
      palette: darkPalette,
    },
    light: {
      palette: lightPalette,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Provider store={store}>
      <CssVarsProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <SessionTimeoutHandler
            timeoutMinutes={SESSION_TIMEOUT_MINUTES}
            warningSeconds={SESSION_TIMEOUT_WARNING_SECONDS}
          >
            <EnvironmentDiagnosticsLogger />
            <VersionChangeHandler />
            <PermissionImpersonationBanner />
            <SysAdminPermissionInspector />
            <RouterProvider router={router} />
          </SessionTimeoutHandler>
        </LocalizationProvider>
      </CssVarsProvider>
    </Provider>
  </React.StrictMode>,
);
